// src/components/documents/DocumentSkeleton.jsx - V1 ENTERPRISE
// ═══════════════════════════════════════════════════════════════════════════
// 💀 DOCUMENT SKELETON — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 ULTRA ROBUSTO:    Skeleton responsivo con shimmer animation
// 🔥 ULTRA RÁPIDO:     GPU-accelerated (solo transform + opacity)
// 🔥 ULTRA EFICIENTE:  React.memo + zero estado interno
// 🔥 ULTRA PROFESIONAL: Simula exactamente la UI final (sin layout shift)
// ═══════════════════════════════════════════════════════════════════════════

import React, { memo } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: SKELETON CARD
// ═══════════════════════════════════════════════════════════════════════════

const SkeletonCard = memo(function SkeletonCard({ isMobile }) {
  return (
    <div
      className={`
        bg-white border border-slate-200 rounded-2xl
        ${isMobile ? 'p-3 space-y-3' : 'p-4 space-y-4'}
      `}
    >
      {/* Header: checkbox + badge + favorito */}
      <div className={`flex items-start justify-between ${isMobile ? 'mt-6' : 'mt-8'}`}>
        <div className="flex items-center gap-2">
          {/* Ícono */}
          <div className={`rounded-lg bg-slate-200 animate-shimmer ${isMobile ? 'w-7 h-7' : 'w-9 h-9'}`} />
          {/* Etiqueta */}
          <div className={`rounded-full bg-slate-200 animate-shimmer ${isMobile ? 'w-12 h-4' : 'w-14 h-5'}`} />
        </div>
        {/* Favorito */}
        <div className={`rounded-lg bg-slate-200 animate-shimmer ${isMobile ? 'w-7 h-7' : 'w-8 h-8'}`} />
      </div>

      {/* Título */}
      <div className="space-y-1.5">
        <div className={`rounded bg-slate-200 animate-shimmer w-full ${isMobile ? 'h-4' : 'h-5'}`} />
        <div className={`rounded bg-slate-200 animate-shimmer w-3/4 ${isMobile ? 'h-3' : 'h-4'}`} />
      </div>

      {/* Nombre de archivo */}
      <div className={`rounded bg-slate-100 animate-shimmer w-1/2 ${isMobile ? 'h-3' : 'h-3.5'}`} />

      {/* Thumbnail placeholder */}
      <div className={`
        rounded-xl bg-slate-100 animate-shimmer
        ${isMobile ? 'h-32' : 'aspect-video'}
      `} />

      {/* Metadatos */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className={`rounded-full bg-slate-200 animate-shimmer ${isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
          <div className={`rounded bg-slate-200 animate-shimmer flex-1 ${isMobile ? 'h-3' : 'h-3.5'}`} />
        </div>
        <div className="flex items-center gap-2">
          <div className={`rounded-full bg-slate-200 animate-shimmer ${isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
          <div className={`rounded bg-slate-200 animate-shimmer w-2/3 ${isMobile ? 'h-3' : 'h-3.5'}`} />
        </div>
        <div className="flex items-center gap-2">
          <div className={`rounded-full bg-slate-200 animate-shimmer ${isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
          <div className={`rounded bg-slate-200 animate-shimmer w-1/3 ${isMobile ? 'h-3' : 'h-3.5'}`} />
        </div>
      </div>

      {/* Acciones */}
      <div className="flex justify-end gap-2 pt-2 border-t border-slate-50">
        <div className={`rounded-xl bg-slate-200 animate-shimmer ${isMobile ? 'w-8 h-8' : 'w-9 h-9'}`} />
        <div className={`rounded-xl bg-slate-200 animate-shimmer ${isMobile ? 'w-8 h-8' : 'w-9 h-9'}`} />
        <div className={`rounded-xl bg-slate-200 animate-shimmer ${isMobile ? 'w-8 h-8' : 'w-9 h-9'}`} />
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 💀 COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const DocumentSkeleton = memo(function DocumentSkeleton({ isMobile }) {
  // Número de skeletons según viewport
  const skeletonCount = isMobile ? 2 : 8;

  return (
    <div className={isMobile ? 'p-3 space-y-4 max-w-7xl mx-auto' : 'p-4 md:p-6 space-y-6 max-w-7xl mx-auto'}>
      {/* Header skeleton */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="space-y-2 w-full sm:w-auto">
          <div
            className={`bg-gradient-to-r from-slate-200 to-slate-300 rounded-lg animate-shimmer ${
              isMobile ? 'h-6 w-32' : 'h-8 w-48'
            }`}
          />
          {!isMobile && (
            <div className="h-4 w-64 bg-gradient-to-r from-slate-100 to-slate-200 rounded-lg animate-shimmer" />
          )}
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div
            className={`bg-slate-200 rounded-xl animate-shimmer ${
              isMobile ? 'h-10 flex-1' : 'h-10 w-64'
            }`}
          />
          <div className="h-10 w-10 bg-slate-200 rounded-xl animate-shimmer" />
        </div>
      </div>

      {/* Stats skeleton */}
      <div className={`grid ${isMobile ? 'grid-cols-2 gap-2' : 'grid-cols-2 md:grid-cols-5 gap-4'}`}>
        {Array.from({ length: isMobile ? 4 : 5 }).map((_, i) => (
          <div
            key={`stat-skel-${i}`}
            className={`bg-slate-200 rounded-xl animate-shimmer ${
              isMobile ? 'h-16' : 'h-24'
            }`}
          />
        ))}
      </div>

      {/* Filters skeleton */}
      <div className="h-12 bg-slate-200 rounded-xl animate-shimmer" />

      {/* Grid skeleton */}
      <div
        className={`grid ${
          isMobile
            ? 'grid-cols-1 gap-3'
            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6'
        }`}
      >
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <SkeletonCard key={`card-skel-${i}`} isMobile={isMobile} />
        ))}
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

DocumentSkeleton.displayName = 'DocumentSkeleton';
export default DocumentSkeleton;